import { useEffect, useMemo, useState } from "react";
import { Pulse, ArrowsClockwise, Sparkle, ListChecks, ChartBar } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const fmtUSD = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtCents = (cents) => `${(cents || 0).toFixed(1)}¢`;

const RANGE_OPTS = [
  { id: 1, label: "24h" },
  { id: 7, label: "7d" },
  { id: 30, label: "30d" },
];

export default function CostCacheDiag({ siloId = null, defaultDays = 7 }) {
  const [days, setDays] = useState(defaultDays);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const params = { days };
      if (siloId) params.silo_id = siloId;
      const r = await api.get("/usage/me/diagnostics", { params });
      setData(r.data);
    } catch { /* silent */ }
    finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  };
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [days, siloId]);

  // Cost trend sparkline
  const sparkline = useMemo(() => {
    const pts = data?.cost_trend || [];
    if (pts.length === 0) return null;
    const w = 200, h = 32;
    const max = Math.max(...pts.map((p) => p.cost_cents), 1);
    const xStep = pts.length > 1 ? w / (pts.length - 1) : w;
    const path = pts.map((p, i) => {
      const x = i * xStep;
      const y = h - (p.cost_cents / max) * (h - 4) - 2;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    return { path, w, h };
  }, [data]);

  const cacheRatio = Math.round((data?.cache_hit_ratio || 0) * 100);
  const tier3Ratio = Math.round((data?.used_tier3_ratio || 0) * 100);
  const askCount = data?.ask_calls ?? 0;
  const noBYOK = data && data.has_byok === false;

  return (
    <div className="bg-white border hairline rounded-xl p-6" data-testid="cost-cache-diag">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
          <Pulse size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-medium text-[#1C1B1A]">Cost & Cache diagnostics</div>
              <div className="text-xs text-[#6B6A68] mt-0.5">
                Per-call breakdown — verify RAG savings, cache reuse, and tier consumption shape over time.
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {RANGE_OPTS.map((r) => (
                <button key={r.id} onClick={() => setDays(r.id)} data-testid={`diag-range-${r.id}`}
                  className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${days === r.id ? "border-[#4A5D4E] bg-[#F0F1EE] text-[#1C1B1A]" : "hairline text-[#6B6A68] hover:border-[#4A5D4E]/40"}`}>
                  {r.label}
                </button>
              ))}
              <button onClick={() => load(true)} title="Refresh" data-testid="diag-refresh"
                className="text-[#6B6A68] hover:text-[#1C1B1A] p-1.5 rounded-md hover:bg-[#F0F1EE]">
                <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          {askCount === 0 && !loading ? (
            <p className="text-sm text-[#6B6A68] mt-5 italic">
              No /ask calls in this window yet. Run a few questions on a silo to populate diagnostics.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-5">
                <Stat
                  testid="diag-stat-calls"
                  label="/ask calls"
                  value={loading ? "…" : askCount.toLocaleString()}
                  icon={<ListChecks size={11} weight="duotone" />}
                />
                <Stat
                  testid="diag-stat-avg-cost"
                  label="Avg cost / call"
                  value={loading ? "…" : fmtCents(data.avg_cost_cents)}
                  icon={<Sparkle size={11} weight="duotone" />}
                  hint={data ? `${fmtUSD(data.total_cost_cents)} total` : null}
                />
                <Stat
                  testid="diag-stat-cache"
                  label="Cache hit ratio"
                  value={loading ? "…" : `${cacheRatio}%`}
                  hint={data ? `${data.cache_hits} hit · ${data.cache_misses} miss` : null}
                  tone={cacheRatio >= 50 ? "good" : cacheRatio >= 20 ? "warn" : "neutral"}
                />
                <Stat
                  testid="diag-stat-input"
                  label="Avg input chars"
                  value={loading ? "…" : (data.avg_input_chars || 0).toLocaleString()}
                  hint={data ? `${(data.avg_output_chars || 0).toLocaleString()} out` : null}
                />
                <Stat
                  testid="diag-stat-tier3"
                  label="Tier 3 used"
                  value={loading ? "…" : `${tier3Ratio}%`}
                  hint={`of /ask calls`}
                  tone={tier3Ratio < 30 ? "good" : tier3Ratio < 60 ? "warn" : "alert"}
                />
              </div>

              {/* Tier consumption bar */}
              {data && (data.avg_tier1_chars + data.avg_tier2_chars + data.avg_tier3_chars > 0) && (
                <div className="mt-5" data-testid="diag-tier-bar">
                  <div className="flex items-center justify-between text-[11px] text-[#6B6A68] mb-1.5">
                    <span>Avg context per call (chars by tier)</span>
                    <span>{(data.avg_tier1_chars + data.avg_tier2_chars + data.avg_tier3_chars).toLocaleString()}</span>
                  </div>
                  {(() => {
                    const t1 = data.avg_tier1_chars || 0;
                    const t2 = data.avg_tier2_chars || 0;
                    const t3 = data.avg_tier3_chars || 0;
                    const total = Math.max(1, t1 + t2 + t3);
                    return (
                      <div className="h-2.5 w-full rounded-full overflow-hidden flex bg-[#F0F1EE]">
                        <div className="h-full" style={{ width: `${(t1 / total) * 100}%`, background: "#1C1B1A" }} title={`Tier 1 (Anchor): ${t1.toLocaleString()} chars`} data-testid="diag-bar-t1" />
                        <div className="h-full" style={{ width: `${(t2 / total) * 100}%`, background: "#4A5D4E" }} title={`Tier 2 (Library): ${t2.toLocaleString()} chars`} data-testid="diag-bar-t2" />
                        <div className="h-full" style={{ width: `${(t3 / total) * 100}%`, background: "#A8B49B" }} title={`Tier 3 (Archive): ${t3.toLocaleString()} chars`} data-testid="diag-bar-t3" />
                      </div>
                    );
                  })()}
                  <div className="flex items-center gap-4 text-[10px] text-[#6B6A68] mt-2">
                    <Legend swatch="#1C1B1A" label="Tier 1 · Anchor" value={(data.avg_tier1_chars || 0).toLocaleString()} />
                    <Legend swatch="#4A5D4E" label="Tier 2 · Library" value={(data.avg_tier2_chars || 0).toLocaleString()} />
                    <Legend swatch="#A8B49B" label="Tier 3 · Archive" value={(data.avg_tier3_chars || 0).toLocaleString()} />
                  </div>
                </div>
              )}

              {/* Cost trend sparkline */}
              {sparkline && data.cost_trend.length > 1 && (
                <div className="mt-5" data-testid="diag-trend">
                  <div className="flex items-center justify-between text-[11px] text-[#6B6A68] mb-1.5">
                    <span className="flex items-center gap-1.5"><ChartBar size={11} weight="duotone" /> Daily cost trend</span>
                    <span>{data.cost_trend.length} days · {fmtUSD(data.total_cost_cents)} total</span>
                  </div>
                  <svg viewBox={`0 0 ${sparkline.w} ${sparkline.h}`} className="w-full h-10" preserveAspectRatio="none">
                    <path d={sparkline.path} fill="none" stroke="#4A5D4E" strokeWidth="1.5" />
                    <path d={`${sparkline.path} L${sparkline.w},${sparkline.h} L0,${sparkline.h} Z`} fill="#4A5D4E" opacity="0.08" />
                  </svg>
                </div>
              )}

              {noBYOK && (
                <p className="text-[11px] text-[#C16E5A] mt-4">
                  No BYOK key set — Gemini Explicit Context Caching is unavailable on the platform fallback path. Add your Google Gemini API key to unlock the cache hit ratio.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ testid, label, value, hint, icon, tone = "neutral" }) {
  const toneColor = tone === "good" ? "text-[#4A5D4E]" : tone === "alert" ? "text-[#C16E5A]" : tone === "warn" ? "text-[#D4A24E]" : "text-[#1C1B1A]";
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] flex items-center gap-1">{icon}{label}</div>
      <div className={`font-serif-display text-2xl mt-1 ${toneColor}`}>{value}</div>
      {hint && <div className="text-[10px] text-[#6B6A68] mt-0.5">{hint}</div>}
    </div>
  );
}

function Legend({ swatch, label, value }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-sm inline-block" style={{ background: swatch }} />
      <span>{label}</span>
      <span className="text-[#1C1B1A]">{value}</span>
    </span>
  );
}
